import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const WorkQueue = ({ transactions, onBulkAction, onTransactionClick }) => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [filterRisk, setFilterRisk] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('priority');

  const riskOptions = [
    { value: 'all', label: 'All Risk Levels' },
    { value: 'high', label: 'High Risk' },
    { value: 'medium', label: 'Medium Risk' },
    { value: 'low', label: 'Low Risk' }
  ];

  const typeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'kyc', label: 'KYC Review' },
    { value: 'transaction', label: 'Transaction Review' },
    { value: 'document', label: 'Document Verification' }
  ];

  const sortOptions = [
    { value: 'priority', label: 'Priority' },
    { value: 'age', label: 'Age' },
    { value: 'amount', label: 'Amount' },
    { value: 'risk', label: 'Risk Score' }
  ];

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'high': return 'text-error bg-error/10';
      case 'medium': return 'text-warning bg-warning/10';
      case 'low': return 'text-success bg-success/10';
      default: return 'text-muted-foreground bg-muted/10';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-warning bg-warning/10';
      case 'review': return 'text-accent bg-accent/10';
      case 'flagged': return 'text-error bg-error/10';
      default: return 'text-muted-foreground bg-muted/10';
    }
  };

  const filteredTransactions = transactions?.filter(transaction => {
    if (filterRisk !== 'all' && transaction?.riskLevel !== filterRisk) return false;
    if (filterType !== 'all' && transaction?.type !== filterType) return false;
    return true;
  });

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedItems(filteredTransactions?.map(t => t?.id));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (id, checked) => {
    if (checked) {
      setSelectedItems([...selectedItems, id]);
    } else {
      setSelectedItems(selectedItems?.filter(item => item !== id));
    }
  };

  const handleBulkAction = (action) => {
    onBulkAction(action, selectedItems);
    setSelectedItems([]);
  };

  return (
    <div className="bg-card border border-border rounded-lg">
      <div className="p-4 border-b border-border">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between space-y-4 lg:space-y-0">
          <h3 className="text-lg font-semibold text-foreground">Work Queue</h3>
          
          <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
            <Select
              options={riskOptions}
              value={filterRisk}
              onChange={setFilterRisk}
              placeholder="Filter by risk"
              className="w-full sm:w-auto"
            />
            <Select
              options={typeOptions}
              value={filterType}
              onChange={setFilterType}
              placeholder="Filter by type"
              className="w-full sm:w-auto"
            />
            <Select
              options={sortOptions}
              value={sortBy}
              onChange={setSortBy}
              placeholder="Sort by"
              className="w-full sm:w-auto"
            />
          </div>
        </div>

        {selectedItems?.length > 0 && (
          <div className="mt-4 p-3 bg-muted rounded-lg">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-2 sm:space-y-0">
              <span className="text-sm font-medium text-foreground">
                {selectedItems?.length} items selected
              </span>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkAction('approve')}
                  iconName="Check"
                  iconPosition="left"
                  iconSize={14}
                >
                  Approve
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkAction('reject')}
                  iconName="X"
                  iconPosition="left"
                  iconSize={14}
                >
                  Reject
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkAction('flag')}
                  iconName="Flag"
                  iconPosition="left"
                  iconSize={14}
                >
                  Flag
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="p-3 text-left">
                <Checkbox
                  checked={selectedItems?.length === filteredTransactions?.length && filteredTransactions?.length > 0}
                  onChange={(e) => handleSelectAll(e?.target?.checked)}
                />
              </th>
              <th className="p-3 text-left text-sm font-medium text-muted-foreground">Transaction</th>
              <th className="p-3 text-left text-sm font-medium text-muted-foreground">Customer</th>
              <th className="p-3 text-left text-sm font-medium text-muted-foreground">Amount</th>
              <th className="p-3 text-left text-sm font-medium text-muted-foreground">Risk</th>
              <th className="p-3 text-left text-sm font-medium text-muted-foreground">Status</th>
              <th className="p-3 text-left text-sm font-medium text-muted-foreground">Age</th>
              <th className="p-3 text-left text-sm font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filteredTransactions?.map((transaction) => (
              <tr
                key={transaction?.id}
                className="hover:bg-muted/50 cursor-pointer transition-colors duration-150"
                onClick={() => onTransactionClick(transaction)}
              >
                <td className="p-3" onClick={(e) => e?.stopPropagation()}>
                  <Checkbox
                    checked={selectedItems?.includes(transaction?.id)}
                    onChange={(e) => handleSelectItem(transaction?.id, e?.target?.checked)}
                  />
                </td>
                <td className="p-3">
                  <div>
                    <p className="font-medium text-foreground">{transaction?.id}</p>
                    <p className="text-sm text-muted-foreground">{transaction?.type}</p>
                  </div>
                </td>
                <td className="p-3">
                  <div>
                    <p className="font-medium text-foreground">{transaction?.customerName}</p>
                    <p className="text-sm text-muted-foreground">{transaction?.customerEmail}</p>
                  </div>
                </td>
                <td className="p-3">
                  <p className="font-medium text-foreground">{transaction?.amount}</p>
                  <p className="text-sm text-muted-foreground">{transaction?.currency}</p>
                </td>
                <td className="p-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRiskColor(transaction?.riskLevel)}`}>
                    {transaction?.riskScore}/100
                  </span>
                </td>
                <td className="p-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(transaction?.status)}`}>
                    {transaction?.status}
                  </span>
                </td>
                <td className="p-3">
                  <p className="text-sm text-muted-foreground">{transaction?.age}</p>
                </td>
                <td className="p-3" onClick={(e) => e?.stopPropagation()}>
                  <div className="flex items-center space-x-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onTransactionClick(transaction)}
                      iconName="Eye"
                      iconSize={14}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleBulkAction('approve', [transaction?.id])}
                      iconName="Check"
                      iconSize={14}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleBulkAction('reject', [transaction?.id])}
                      iconName="X"
                      iconSize={14}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {filteredTransactions?.length === 0 && (
        <div className="p-8 text-center">
          <Icon name="Inbox" size={48} className="mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No transactions match the current filters</p>
        </div>
      )}
    </div>
  );
};

export default WorkQueue;